/**
 * Rules D-6 and D-6b explanation assembly (S3-01).
 * DEC-31, as restated in PRS VERSION 1.0 UXP-07: a canonical grade label
 * cited inside a rule D-6 OR D-6b explanation is permitted and required.
 * Such an explanation carries its assessment date and its competency, and
 * under rule D-6 the observed behaviors. The templates below are the only
 * places a grade label appears in policy output.
 *
 * Both templates render US spelling, following director ruling D1 of
 * 17 August 2026 as applied to the operative text at PRS VERSION 1.0.
 * Identifiers are unchanged by that ruling, so the field and parameter
 * names around these strings keep their spelling; only the rendered text
 * moves.
 */
import { GRADE_LABEL_BY_VALUE, GradeLabelV1 } from '../icd/enums';
import type { DrivingEvidence, GradeLabel } from './types';

/**
 * Content owner's ruling of 16 August 2026: any text a trainee sees under
 * D-6 or D-6b renders singular at a count of 1; zero and two or more stay
 * plural. The stored explanation strings are trainee-facing text, so this
 * builder is the layer that pluralizes. Verb agreement follows the noun it
 * governs, because "1 additional item address ..." is not text a trainee
 * can be shown; the ruling names the noun forms and this is its reading for
 * the one verb the template carries. Rule D-6b carries no count, so the
 * ruling is inapplicable to it rather than unapplied (confirmed by the
 * content approver, 24 August 2026, and unchanged by DEC-48).
 */
function counted(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * The five canonical grade labels of PRS section 4.1, taken from the ICD
 * enum rather than transcribed here, so there is one list and not a second
 * one that can drift from it.
 */
const CANONICAL_GRADE_LABELS: readonly string[] = GradeLabelV1.options;

/**
 * DEC-31, as restated in PRS VERSION 1.0 UXP-07, makes the label REQUIRED
 * inside a D-6 or D-6b explanation and not merely permitted there. A label
 * that does not resolve to one of the five is therefore an engine defect,
 * and this is where it stops: no empty slot, no "undefined" in trainee
 * copy, and no silent fall back to a sentence that omits the label
 * (content approver, 26 August 2026). The check is on the rendered value,
 * not on the type, because the type is what a defect upstream has already
 * lied about by the time this is reached.
 */
function requireCanonicalLabel(gradeLabel: GradeLabel): GradeLabel {
  if (!CANONICAL_GRADE_LABELS.includes(gradeLabel)) {
    throw new Error(
      `policy defect: no canonical grade label resolved for a rule D-6/D-6b ` +
        `explanation (received ${JSON.stringify(gradeLabel)}); DEC-31 requires ` +
        `one of ${CANONICAL_GRADE_LABELS.join(', ')}`,
    );
  }
  return gradeLabel;
}

/**
 * The lead sentence both templates share (PRS VERSION 1.0 Appendix D):
 * "Your session on [date] recorded [grade label] for [competency], with
 * ... noted." D-6 fills the middle with the observed-behavior count; D-6b
 * fills it with "no specific behaviors" and carries no count (DEC-48).
 */
function lead(
  assessmentDate: string,
  gradeLabel: GradeLabel,
  competencyName: string,
  middle: string,
): string {
  const label = requireCanonicalLabel(gradeLabel);
  return (
    `Your session on ${assessmentDate} recorded ${label} for ` +
    `${competencyName}, with ${middle} noted.`
  );
}

/**
 * D-6 template (UXP-03): "Your session on [date] recorded [grade label] for
 * [competency], with [n] observed behaviors noted. This pathway practices
 * [learning objective], and [m] additional items address [cited
 * behaviors]."
 *
 * Rendering decisions (documented in the module README): [date] is the
 * source assessment date as supplied (YYYY-MM-DD); [competency] is the
 * registry display name; [cited behaviors] are the cited codes joined by
 * ", "; when no supplement object is attached (m = 0) the final clause is
 * omitted because the template's list slot would otherwise render empty;
 * n = 1 and m = 1 render singular per the 16 August 2026 ruling.
 */
export function buildExplanation(
  evidence: DrivingEvidence,
  competencyName: string,
  learningObjective: string,
  attachedSupplementCount: number,
): string {
  const label = GRADE_LABEL_BY_VALUE[evidence.grade as 1 | 2 | 3 | 4 | 5];
  const observed = counted(
    evidence.cited_behaviours.length,
    'observed behavior',
    'observed behaviors',
  );
  const opening = lead(evidence.assessment_date, label, competencyName, observed);
  if (attachedSupplementCount === 0) {
    return `${opening} This pathway practices ${learningObjective}.`;
  }
  const cited = evidence.cited_behaviours.join(', ');
  const items = counted(attachedSupplementCount, 'additional item', 'additional items');
  const addresses = attachedSupplementCount === 1 ? 'addresses' : 'address';
  return (
    `${opening} This pathway practices ${learningObjective}, and ` +
    `${items} ${addresses} ${cited}.`
  );
}

/**
 * D-6b fallback template, used only for a D-3c fallback recommendation.
 * PRS VERSION 1.0 Appendix D, on the wording DEC-48 approved on
 * 26 August 2026: "Your session on [date] recorded [grade label] for
 * [competency], with no specific behaviors noted. This is a development
 * suggestion rather than a response to observed performance."
 *
 * The label is the canonical grade label of PRS section 4.1, and no count
 * enters this template. The no-behavior clause stays, so the REC-02
 * carve-out is unchanged and the recommendation is never presented as
 * evidence-driven (UXP-03, UXP-07).
 *
 * The grade label is a parameter rather than derived here, because the
 * caller already resolves it once onto the record (`grade_label` in
 * `driving_evidence`) and the sentence must not be able to disagree with
 * the field beside it.
 */
export function buildFallbackExplanation(
  gradeLabel: GradeLabel,
  competencyName: string,
  assessmentDate: string,
): string {
  const opening = lead(assessmentDate, gradeLabel, competencyName, 'no specific behaviors');
  return (
    `${opening} This is a development suggestion rather than a response to ` +
    `observed performance.`
  );
}
